import React, { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import { ExternalLink, Clock, History as HistoryIcon } from "lucide-react";
import { loadHistory, updateTxStatus } from "@/lib/history";
import { dexApi } from "@/lib/lifi";
import { EXPLORERS } from "@/components/SwapWidget";

function fmtAgo(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const FINAL = new Set(["DONE", "FAILED"]);

export default function HistoryPanel({ historyVersion }) {
  const { address, isConnected } = useAccount();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!address) return setRows([]);
    setRows(loadHistory(address));
  }, [address, historyVersion]);

  useEffect(() => {
    if (!address || !rows.length) return;
    const pendings = rows.filter((r) => !FINAL.has(r.status));
    if (!pendings.length) return;
    let cancel = false;
    const tick = async () => {
      for (const p of pendings) {
        try {
          const s = await dexApi.status({
            txHash: p.hash, fromChain: p.fromChainId, toChain: p.toChainId,
          });
          if (cancel) return;
          if (s?.status && s.status !== p.status) {
            setRows(updateTxStatus(address, p.hash, { status: s.status }));
          }
        } catch {}
      }
    };
    tick();
    const id = setInterval(tick, 15_000);
    return () => { cancel = true; clearInterval(id); };
  }, [address, rows]);

  if (!isConnected) {
    return (
      <div className="px-4 py-10 text-center">
        <div className="mx-auto w-10 h-10 rounded-lg bg-[var(--panel-2)] border border-[var(--border)] flex items-center justify-center mb-3">
          <HistoryIcon size={16} className="text-[var(--text-muted)]" />
        </div>
        <div className="text-[13px] text-[var(--text)] font-medium">Your swap history</div>
        <div className="text-[12px] text-[var(--text-muted)] mt-1 max-w-[240px] mx-auto">
          Connect your wallet to see your swap history with live status.
        </div>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="px-4 py-10 text-center">
        <div className="mx-auto w-10 h-10 rounded-lg bg-[var(--panel-2)] border border-[var(--border)] flex items-center justify-center mb-3">
          <Clock size={16} className="text-[var(--text-muted)]" />
        </div>
        <div className="text-[13px] text-[var(--text)] font-medium">No swaps yet</div>
        <div className="text-[12px] text-[var(--text-muted)] mt-1 max-w-[240px] mx-auto">
          Your swaps will appear here with block-explorer links once executed.
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-4 py-3">
      <div className="text-[10.5px] text-[var(--text-subtle)] uppercase tracking-wider mb-2">Recent activity · {rows.length}</div>
      <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
        {rows.map((r) => {
          const isDone = r.status === "DONE";
          const isFail = r.status === "FAILED";
          const exp = EXPLORERS[r.fromChainId];
          return (
            <div key={r.hash} className="panel-3 p-3">
              <div className="flex items-center justify-between">
                <div className="text-[12.5px] text-[var(--text)] font-mono truncate">{r.fromSymbol} → {r.toSymbol}</div>
                <span className={`pill ${isDone ? "pill-up" : isFail ? "pill-down" : ""}`}>{isDone ? "Done" : isFail ? "Failed" : r.status || "Pending"}</span>
              </div>
              <div className="text-[11px] text-[var(--text-muted)] mt-1 font-mono truncate">
                {r.fromAmount} {r.fromSymbol} → {r.toAmount ? Number(r.toAmount).toFixed(4) : "…"} {r.toSymbol}
              </div>
              <div className="flex items-center justify-between mt-2 text-[10.5px] text-[var(--text-subtle)] font-mono">
                <span className="truncate">via {r.tool}</span>
                <span>{fmtAgo(r.ts)}</span>
                {exp && (
                  <a href={exp + r.hash} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline flex-shrink-0">
                    tx <ExternalLink size={10} />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
